import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';

import { routes } from '@config/routes';
import { createRouteUrl, useNavigate } from '@lib/routing';
import { useLocationQuery } from '@modules/layout/hooks';
import { Loader } from '@modules/layout/moleculas';
import { useCourse } from '@modules/courses/hooks';
import { useSubject } from '@modules/subjects/main/hooks';
import { useCurrentLap } from '@modules/lap/hooks';
import { StartTestMutation, StopTestMutation } from '@modules/testing/graphql';
import { useTest } from '@modules/testing/hooks';
import { TestingForm, TestingPrepare, handleSetLastUntil } from '@modules/testing/organisms';

import type { CourseEntity } from '@modules/courses/entities';
import type { SubjectEntity } from '@modules/subjects/main/entities';
import type {
    StartTestMutationType,
    StartTestMutationVariables,
    StopTestMutationType,
    StopTestMutationVariables,
} from '@modules/types/graphql';

type TestingProps = {
    subjectId?: string;
    courseId?: string;
    onError: VoidFunction;
    onTestNotAvailable: (course: CourseEntity, subject?: SubjectEntity) => void;
    onSetTitle: (title: string) => void;
    onSetRedirect?: (callback: () => () => void) => void;
    onCancel?: (course: CourseEntity, subject: SubjectEntity) => () => void;
    onClick?: () => void | Promise<void>;
};

const Root = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const Testing = (props: TestingProps): React.ReactElement | null => {
    const {
        subjectId,
        courseId,
        onTestNotAvailable,
        onError,
        onSetTitle,
        onSetRedirect,
        onCancel,
        onClick,
    } = props;

    const { t } = useTranslation(['testing']);

    const navigate = useNavigate();

    const { query, setQuery } = useLocationQuery();

    const options = {
        subjectId: subjectId || (query.subject as string | null),
        courseId: courseId || (query.course ? (query.course as string) : '1'),
    };

    const [canPassTest, setCanPassTest] = React.useState<boolean>(false);

    const { currentLap } = useCurrentLap();

    const {
        subject,
        loading: subjectLoading,
        error: subjectError,
    } = useSubject({
        skip: !options.subjectId || options.subjectId === '0',
        variables: { id: options.subjectId as string },
    });

    const {
        course,
        loading: courseLoading,
        error: courseError,
    } = useCourse({
        skip: !options.courseId,
        variables: { id: options.courseId },
    });

    const {
        test,
        loading: testLoading,
        refetch: testRefetch,
    } = useTest({
        fetchPolicy: 'cache-first',
        notifyOnNetworkStatusChange: true,
        variables: options,
    });

    React.useEffect(() => {
        if (courseError || subjectError) {
            onError();
        }
    }, [courseError, subjectError, onError]);

    React.useEffect(() => {
        if (courseLoading || subjectLoading) {
            return;
        }

        const canPassTest = course.testAvailable;

        if (!subject.exists() && course.exists() && !canPassTest) {
            onTestNotAvailable(course);
        } else if (subject.exists() && course.exists() && !subject.testEnabled) {
            onTestNotAvailable(course, subject);
        } else {
            setCanPassTest(true);
        }
    }, [subject, courseLoading, subjectLoading]);

    React.useEffect(() => {
        if (courseLoading || subjectLoading) {
            return;
        }

        let title = '';
        let redirectUrl = '';

        let handleRedirect = () => {
            navigate(redirectUrl);
        };

        if (subject.exists()) {
            title = t('testing:blocks.titles.testing', { name: subject.name });
            redirectUrl = createRouteUrl(routes.subject, {
                courseId: course.id,
                subjectId: subject.id,
            });
        } else if (course.exists()) {
            title = t('testing:blocks.titles.testing', { name: course.name });
            redirectUrl = createRouteUrl(routes.subjects, { courseId: course.id });
        }

        !!title && onSetTitle?.(title);
        !!redirectUrl && onSetRedirect?.(() => handleRedirect);
    }, [onSetTitle, onSetRedirect, courseLoading, subjectLoading, subject, course]);

    const [startTest, { loading: startTestLoading }] = useMutation<
        StartTestMutationType,
        StartTestMutationVariables
    >(StartTestMutation, {
        variables: options,
        onCompleted: () => {
            testRefetch();
        },
    });

    const [stopTest] = useMutation<StopTestMutationType, StopTestMutationVariables>(
        StopTestMutation,
        {
            variables: options,
            onCompleted: () => {
                handleSetLastUntil(undefined);
                testRefetch();
            },
            onError: () => {
                handleSetLastUntil(undefined);
                testRefetch();
            },
            optimisticResponse: {
                stopOLTest: {
                    __typename: 'OLTest',
                    ...test,
                    finished: true,
                },
            },
        },
    );

    const handleStartTest = async (): Promise<void> => {
        await startTest();
        setQuery({ ...query, question: 1 });
    };

    const handleStopTest = async (): Promise<void> => {
        await stopTest();
        setQuery({ ...query, question: undefined });
    };

    const handleTimeUp = async (): Promise<void> => {
        await stopTest();
        setQuery({ ...query, question: undefined });
    };

    const handleAnswer = async (): Promise<void> => {
        testRefetch();
    };

    const type = React.useMemo(() => {
        const { canStudentViewOLTestResult, studentViewOLTestResultAfterSuccess } = currentLap;
        const isSuccess = test.resultSuccess || course.passTestForAnyResult;

        if (canStudentViewOLTestResult && studentViewOLTestResultAfterSuccess) {
            return isSuccess ? 'successWithResult' : 'failure';
        }

        if (canStudentViewOLTestResult) {
            return isSuccess ? 'successWithResult' : 'failureWithResult';
        }

        return isSuccess ? 'success' : 'failure';
    }, [test.resultSuccess, course.passTestForAnyResult, currentLap]);

    if (courseLoading || subjectLoading || testLoading) {
        return (
            <Root>
                <Loader />
            </Root>
        );
    }

    if (!canPassTest) {
        return null;
    }

    if (!test.exists()) {
        return (
            <Root>
                <TestingPrepare
                    type='default'
                    course={course}
                    subject={subject}
                    loading={startTestLoading}
                    onStart={handleStartTest}
                />
            </Root>
        );
    }

    return (
        <Root>
            {!test.isFinished() ? (
                <TestingForm
                    test={test}
                    onAnswer={handleAnswer}
                    onTimeUp={handleTimeUp}
                    onFinish={handleStopTest}
                />
            ) : (
                <TestingPrepare
                    type={type}
                    test={test}
                    course={course}
                    subject={subject}
                    loading={startTestLoading}
                    onStart={
                        test.resultSuccess || course.passTestForAnyResult
                            ? undefined
                            : handleStartTest
                    }
                    onCancel={onCancel?.(course, subject)}
                    onClick={onClick}
                />
            )}
        </Root>
    );
};

export { Testing };
